/**
 * Acting as another account — an admin looking at a test user's data.
 *
 * ## The server decides; this only asks
 *
 * Setting a value here changes one header. Whether it is *honoured* is
 * `_resolve_act_as` in qv-data: the caller must hold `admin`, the target must be
 * `is_test`, and it may never be another admin (D31). A client that stored the
 * wrong thing gets a 403, not somebody's journal — which is why this module can
 * be as simple as it is.
 *
 * ## Per request, never a server-side mode
 *
 * The header goes on every call rather than the server remembering. A mode you
 * can forget you are in is how an admin deletes the wrong data, and a stored
 * one would survive a reload with nothing on screen to say so. The banner is
 * the other half of that: loud, and present for as long as the header is.
 *
 * ## Remembered across a reload, deliberately — but only for whoever chose it
 *
 * `localStorage`, because the alternative is re-picking the account on every
 * refresh while debugging somebody's layout — which is exactly when you reload
 * constantly.
 *
 * The original reasoning for persisting was that this is *not authority*: the
 * server re-checks every request, so a stale entry is refused rather than
 * obeyed. True, and it missed what a refusal costs. **The entry outlived the
 * session that chose it**: an admin acting as `demo2` signed out, somebody else
 * signed in, and the new account inherited the header — so every request carried
 * `X-Act-As: demo2`, the server correctly answered *"not permitted to act as
 * another user"*, and the app was **entirely unusable** for that user with
 * nothing on screen explaining why. Refused rather than obeyed is the right
 * security outcome and a terrible availability one; the two are not a trade
 * here, because nobody ever wanted to inherit it.
 *
 * So the target is stored **with the uid that chose it**, and
 * `reconcileActingAs` drops it the moment a different account is signed in.
 * A reload by the same admin still keeps it, which is the whole point of
 * persisting. A legacy bare-string entry has no owner recorded and is therefore
 * dropped on the first reconcile — once, for everyone, which is correct: it is
 * exactly the state this fixes.
 */

const KEY = "qufolio-acting-as";

const listeners = new Set<(target: string | null) => void>();

/** The stored choice: who is being acted as, and who asked for it. */
type Held = { target: string; owner: string | null };

function read(): Held | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    if (raw.startsWith("{")) {
      const parsed = JSON.parse(raw) as Partial<Held>;
      if (typeof parsed?.target !== "string" || !parsed.target) return null;
      return { target: parsed.target, owner: parsed.owner ?? null };
    }
    // Written before the target carried its owner. `null` owner matches only a
    // signed-out session, so a signed-in user drops it on the first reconcile.
    return { target: raw, owner: null };
  } catch {
    // Private mode, storage disabled, or unparseable JSON. Not being able to
    // *remember* the choice is survivable; failing to load the app over it is
    // not — and neither is throwing on a value somebody hand-edited.
    return null;
  }
}

let held: Held | null = read();

/** Who is signed in, as last reported by `trackUser`. Stamped onto a new choice. */
let signedInAs: string | null = null;

/** The account being acted as, or `null` for "myself". */
export function actingAs(): string | null {
  return held?.target ?? null;
}

/** Switch, or stop. `null` returns to the signed-in account. */
export function setActingAs(target: string | null): void {
  const wanted = target && target.trim() ? target.trim() : null;
  held = wanted ? { target: wanted, owner: signedInAs } : null;
  try {
    if (held) localStorage.setItem(KEY, JSON.stringify(held));
    else localStorage.removeItem(KEY);
  } catch {
    // See `read` — the choice still applies to this session.
  }
  for (const notify of listeners) notify(actingAs());
}

/**
 * Tell this module who is signed in, dropping a choice that was not theirs.
 *
 * Called from `trackUser` on every auth change, which is the only place that
 * knows. It must run on **sign-in** and not merely on sign-out: signing out
 * usually reloads the page, so the next process starts with the stale entry
 * already read from storage and no sign-out callback to react to. Comparing
 * against the owner covers both routes with one rule.
 *
 * Equal owners keep the choice, `null === null` included, so a developer build
 * with no Firebase behind it behaves exactly as it did.
 */
export function reconcileActingAs(uid: string | null): void {
  signedInAs = uid;
  if (held && held.owner !== uid) setActingAs(null);
}

export function onActingAsChanged(callback: (target: string | null) => void): () => void {
  listeners.add(callback);
  return () => {
    listeners.delete(callback);
  };
}

/**
 * The header, or nothing.
 *
 * An empty object rather than an empty header value, for `authHeaders`' reason:
 * spreading `{}` adds nothing, where `X-Act-As: ""` is a malformed header the
 * server would have to make a decision about.
 */
export function actAsHeaders(): Record<string, string> {
  const target = actingAs();
  return target ? { "X-Act-As": target } : {};
}
